import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import {
  BubbleAccordion,
  BubbleButton,
  BubbleDebounce,
  BubbleCheckbox,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleInput,
  BubbleOverlay,
  BubbleTypeahead,
  BubbleTable,
  BubbleBadge,
} from '@go-client/components/BubbleUI';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import './GroupsHome.scss';

import { useUserInfo } from '@go-client/context/UserInfoContext';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import GroupsNavigation from '@go-client/components/Navigations/GroupsNavigation';
import PageWrapper from './PageWrapper';
import Page from './Page';
import { MenuBar } from '@go-client/components';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { groupResourceClient } from '@go-client/lib/api/v2/groupResource';
import GroupResourceForm, {
  GroupsResourceMode,
} from '@go-client/components/GroupBookingForm/GroupResourceForm';
import {
  BubbleTypeaheadOption,
  BubbleTypeaheadValue,
} from '@go-client/components/BubbleUI/BubbleTypeahead';
import uuid from 'uuid';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import { useGroupResourceData } from '@go-client/hooks/useGroupResourceData';
import { canCreateOrEditGroupResources } from '@go-shared/lib/permissions';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { hashStringToColor } from '@go-shared/lib/utils';
import { GroupResource } from '@go-shared/types/zod/groupResource';
import { getLocaleDateTimeStringUTC, isoToDateOptionalUTC } from '@go-shared/lib/time';

type ResourceTableItem = Omit<GroupResource, '_id'> & {
  id: string;
  color: string;
};

interface GroupsFilters {
  selectedTypes: Array<BubbleTypeaheadValue<string>>;
  updatedAt: { startDate?: string; endDate?: string };
  createdAt: { startDate?: string; endDate?: string };
  users: Array<BubbleTypeaheadValue<string>>;
}

function makeBadgeFromType(resource: ResourceTableItem) {
  const { resourceType, color } = resource;

  return <BubbleBadge color={color}>{resourceType}</BubbleBadge>;
}

function renderDate(fullDate: Date) {
  let element = <></>;

  if (fullDate) {
    const updatedAt = getLocaleDateTimeStringUTC(fullDate);
    const [date, time] = updatedAt.split(', ');
    element = (
      <>
        <div className="block whitespace-nowrap">{date}</div>
        <div className="block whitespace-nowrap">{time}</div>
      </>
    );
  }

  return element;
}

interface User {
  _id: string;
  userId: string;
  firstName: string;
  lastName: string;
}

function getUniqueUsers(resources: GroupResource[]): User[] {
  const usersMap = new Map<string, User>();

  // We add the user info to the map by userId. No duplicates
  resources.forEach(resource => {
    usersMap.set(resource.updatedBy.userId, resource.updatedBy);
    usersMap.set(resource.createdBy.userId, resource.createdBy);
  });

  return Array.from(usersMap.values());
}

export default function ResourceLibrary(props: RouteComponentProps) {
  const { activeProperty } = useActiveProperty();

  const { user } = useUserInfo();
  const serverInfo = useServerInfo();
  const [search, setSearch] = useState('');
  const [showArchive, setShowArchive] = useState<{ [key: string]: boolean }>({});
  const [mode, setMode] = useState<GroupsResourceMode>({ type: 'default' });
  const [filters, setFilters] = useState<GroupsFilters>({
    selectedTypes: [],
    users: [],
    updatedAt: { startDate: '', endDate: '' },
    createdAt: { startDate: '', endDate: '' },
  });
  const [resourceType, setResourceType] = useState<Array<BubbleTypeaheadOption<string>>>([]);
  const [users, setUsers] = useState<Array<BubbleTypeaheadOption<string>>>([]);
  const [showOnlyArchived, setShowOnlyArchived] = useState(false);

  const { resourceData, loading, refresh } = useGroupResourceData(serverInfo.brand._id);

  useEffect(() => {
    const resourceTypeOptions = [
      ...resourceData.resourceTypes,
      { name: 'unassigned', _id: uuid() },
    ].map(s => ({
      key: s.name,
      item: s.name,
    }));

    const uniqueUsers = getUniqueUsers(resourceData.resources).map(user => ({
      key: user.userId,
      refKey: user.userId,
      item: `${user.lastName}, ${user.firstName}`,
    }));

    setUsers(uniqueUsers);

    setResourceType(resourceTypeOptions);

    const resourceTypeMap = mapBy(resourceTypeOptions, 'key');

    setFilters({
      ...filters,
      selectedTypes: resourceTypeOptions
        .map(sopt => {
          const refOpt = resourceTypeMap[sopt.key];
          return refOpt
            ? {
                key: uuid(),
                item: sopt.item,
                refKey: refOpt.key,
              }
            : undefined;
        })
        .filter(notUndefined),
    });
  }, [resourceData.resourceTypes]);

  const filteredResources = filterResources();
  const tableFields: Array<BubbleTableFields<ResourceTableItem>> = [
    {
      key: 'name',
      label: 'Name',
    },
    {
      key: 'type',
      label: 'Type',
    },
    {
      key: 'price',
      label: 'Price',
      behavior: BubbleTableFieldBehavior.NoWrap,
    },
    { key: 'updatedBy', label: 'Updated By', thProps: { className: 'whitespace-nowrap' } },
    { key: 'updatedAt', label: 'Updated At', thProps: { className: 'whitespace-nowrap' } },
    { key: 'createdBy', label: 'Created By', thProps: { className: 'whitespace-nowrap' } },
    { key: 'createdAt', label: 'Created At', thProps: { className: 'whitespace-nowrap' } },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<ResourceTableItem>> = [
    {
      key: 'name',
      render: item => <> {item.name}</>,
    },
    {
      key: 'type',
      render: item => makeBadgeFromType(item),
    },
    {
      key: 'price',
      render: item => <>${item.price}</>,
    },
    {
      key: 'createdBy',
      render: item => (
        <>
          {item.createdBy.firstName} {item.createdBy.lastName}
        </>
      ),
    },
    {
      key: 'updatedBy',
      render: item => (
        <>
          {item.updatedBy.firstName} {item.updatedBy.lastName}
        </>
      ),
    },
    {
      key: 'updatedAt',
      render: item => renderDate(item.updatedAt),
    },
    {
      key: 'createdAt',
      render: item => renderDate(item.createdAt),
    },
    {
      key: 'actions',
      render: item =>
        canCreateOrEditGroupResources(user, serverInfo) && (
          <>
            <BubbleButton
              onClick={() => handleEdit(item)}
              className="p-2-i"
              variant="navy"
              inverse
              disabled={false}
              icon="edit"
            />
            <BubbleButton
              onClick={() => handleClone(item)}
              className="p-2-i"
              variant="slate"
              inverse
              disabled={false}
              icon="copy"
            />
            {!showArchive[item.id] && (
              <BubbleButton
                className="p-2-i"
                variant="dirt"
                inverse
                onClick={() => {
                  setShowArchive({ ...showArchive, [item.id]: true });
                }}
                icon="trash-alt"
              />
            )}
            {showArchive[item.id] && (
              <>
                <BubbleButton
                  className="p-2-i"
                  variant="crimson"
                  inverse
                  onClick={() => handleArchive(item)}
                  icon="trash-check"
                />
                <BubbleButton
                  className="p-2-i"
                  variant="dirt"
                  inverse
                  onClick={() => {
                    setShowArchive({ ...showArchive, [item.id]: false });
                  }}
                  icon="trash-xmark"
                />
              </>
            )}
          </>
        ),
    },
  ];

  function filterResources() {
    const selectedTypeKeys = new Set(filters.selectedTypes.map(t => t.item.toLowerCase()));
    const normalizedSearch = search.trim().toLowerCase();
    const filteredUserIds = filters.users.map(user => {
      return user.refKey;
    });
    const filteredUserIdsSet = new Set(filteredUserIds);

    return resourceData.resources
      .filter(resource => {
        // Resource type filter
        const typeMatch =
          selectedTypeKeys.size === 0 || selectedTypeKeys.has(resource.resourceType.toLowerCase());

        // Name filter
        const searchMatch = resource.name.toLowerCase().includes(normalizedSearch);

        // User filter
        const userMatch =
          filteredUserIdsSet.size === 0 ||
          filteredUserIdsSet.has(resource.createdBy.userId) ||
          filteredUserIdsSet.has(resource.updatedBy.userId);

        // Archived only filter
        const archivedMatch = showOnlyArchived
          ? resource.archived === true
          : resource.archived === false;

        // CreatedAt Date filter
        let createdDateMatch = true;
        if (filters.createdAt.startDate) {
          if (!isoToDateOptionalUTC(filters.createdAt.startDate)) createdDateMatch = false;
          const startDate = new Date(`${filters.createdAt.startDate}T00:00:00Z`);
          createdDateMatch = new Date(resource.createdAt) >= startDate;
        }

        if (filters.createdAt.endDate) {
          if (!isoToDateOptionalUTC(filters.createdAt.endDate)) createdDateMatch = false;
          const endDate = new Date(`${filters.createdAt.endDate}T23:59:59.999Z`);
          createdDateMatch = createdDateMatch && new Date(resource.createdAt) <= endDate;
        }

        // UpdatedAt Date filter
        let updatedDateMatch = true;
        if (filters.updatedAt.startDate) {
          if (!isoToDateOptionalUTC(filters.updatedAt.startDate)) createdDateMatch = false;
          const startDate = new Date(`${filters.updatedAt.startDate}T00:00:00Z`);
          updatedDateMatch = new Date(resource.updatedAt) >= startDate;
        }

        if (filters.updatedAt.endDate) {
          if (!isoToDateOptionalUTC(filters.updatedAt.endDate)) createdDateMatch = false;
          const endDate = new Date(`${filters.updatedAt.endDate}T23:59:59.999Z`);
          updatedDateMatch = updatedDateMatch && new Date(resource.updatedAt) <= endDate;
        }

        return (
          typeMatch &&
          searchMatch &&
          archivedMatch &&
          createdDateMatch &&
          updatedDateMatch &&
          userMatch
        );
      })
      .map(resource => ({
        ...resource,
        id: resource._id,
        color: hashStringToColor(resource.resourceType),
      }));
  }

  async function handleArchive(item: ResourceTableItem) {
    await groupResourceClient.archive({
      params: { resourceId: item.id },
      body: { archived: !item.archived },
    });
    refresh();
  }

  function handleEdit(item: {
    id: string;
    name: string;
    price: number;
    resourceType: string;
  }): void {
    setMode({ type: 'edit', data: item });
  }

  function handleClone(item: {
    id: string;
    name: string;
    price: number;
    resourceType: string;
  }): void {
    setMode({ type: 'clone', data: item });
  }

  function handleCloseGroupResourceForm() {
    setMode({ type: 'default' });
  }

  return (
    <>
      <PageWrapper {...props}>
        <Page className="Groups absolute flex items-stretch">
          <MenuBar {...props} property={activeProperty} />
          <div className="SideMenu bg-forest-darkest flex flex-col">
            <div className="bg-white flex flex-col items-center">
              <BrandLogo logo={activeProperty.brand.logo} />
              <PropertyBanner {...props} property={activeProperty} swappable />
            </div>
            <div className={`flex flex-col relative items-stretch p-4 bg-forest-darker`}>
              <div className="grid gap-2">
                {canCreateOrEditGroupResources(user, serverInfo) && (
                  <BubbleButton
                    variant="lime"
                    className="text-left"
                    block
                    icon="plus"
                    onClick={() => {
                      setMode({ type: 'create' });
                    }}
                  >
                    New Resource
                  </BubbleButton>
                )}
              </div>
            </div>
            <BubbleAccordion
              variant="forest"
              className="flex-grow flex flex-col border-t border-forest-darkest"
              labels={['Filters']}
              open={[0]}
              locked={[0]}
            >
              <div className="grid gap-4 p-4">
                <div className="dropdown-inline-fix top-0 z-10 bg-forest-darkest">
                  <BubbleFormGroup label="Resource Types" labelClassName="text-white-i">
                    <BubbleTypeahead
                      block
                      variant="forest"
                      placeholder="Filter types shown..."
                      menuClassName="max-h-80 overflow-y-auto"
                      value={filters.selectedTypes}
                      options={resourceType}
                      onAdd={v =>
                        setFilters({
                          ...filters,
                          selectedTypes: [...filters.selectedTypes, v],
                        })
                      }
                      onRemove={v =>
                        setFilters({
                          ...filters,
                          selectedTypes: filters.selectedTypes.filter(v2 => v2.key !== v.key),
                        })
                      }
                      onClearAll={() => setFilters({ ...filters, selectedTypes: [] })}
                    />
                  </BubbleFormGroup>
                </div>
                <BubbleFormGroup label="Updated At" labelClassName="text-white-i">
                  <div className="grid grid-cols-1 gap-1">
                    <BubbleDebounce
                      onTrigger={[
                        (date: string) => {
                          setFilters(prev => ({
                            ...prev,
                            updatedAt: { ...prev.updatedAt, startDate: date },
                          }));
                        },
                        500,
                      ]}
                    >
                      {({ debounce }) => (
                        <BubbleInput
                          variant="forest"
                          type="date"
                          value={filters.updatedAt?.startDate}
                          onValue={debounce}
                        />
                      )}
                    </BubbleDebounce>
                    <BubbleDebounce
                      onTrigger={[
                        (date: string) => {
                          setFilters(prev => ({
                            ...prev,
                            updatedAt: { ...prev.updatedAt, endDate: date },
                          }));
                        },
                        500,
                      ]}
                    >
                      {({ debounce }) => (
                        <BubbleInput
                          variant="forest"
                          type="date"
                          value={filters.updatedAt?.endDate}
                          onValue={debounce}
                        />
                      )}
                    </BubbleDebounce>
                  </div>
                </BubbleFormGroup>
                <BubbleFormGroup label="Created At" labelClassName="text-white-i">
                  <div className="grid grid-cols-1 gap-1">
                    <BubbleDebounce
                      onTrigger={[
                        (date: string) => {
                          setFilters(prev => ({
                            ...prev,
                            createdAt: { ...prev.createdAt, startDate: date },
                          }));
                        },
                        500,
                      ]}
                    >
                      {({ debounce }) => (
                        <BubbleInput
                          variant="forest"
                          type="date"
                          value={filters.createdAt.startDate}
                          onValue={debounce}
                        />
                      )}
                    </BubbleDebounce>
                    <BubbleDebounce
                      onTrigger={[
                        (date: string) => {
                          setFilters(prev => ({
                            ...prev,
                            createdAt: { ...prev.createdAt, endDate: date },
                          }));
                        },
                        500,
                      ]}
                    >
                      {({ debounce }) => (
                        <BubbleInput
                          variant="forest"
                          type="date"
                          value={filters.createdAt.endDate}
                          onValue={debounce}
                        />
                      )}
                    </BubbleDebounce>
                  </div>
                </BubbleFormGroup>

                <BubbleFormGroup label="Users" labelClassName="text-white-i">
                  <BubbleTypeahead
                    block
                    variant="forest"
                    placeholder="Filter users"
                    menuClassName="max-h-80 overflow-y-auto"
                    value={filters.users}
                    options={users}
                    labelExtractor={l => l}
                    onAdd={v => {
                      setFilters({
                        ...filters,
                        users: [...filters.users, v],
                      });
                    }}
                    onRemove={v => {
                      setFilters({
                        ...filters,
                        users: filters.users.filter(v2 => v2.key !== v.key),
                      });
                    }}
                    onClearAll={() => {
                      setFilters({ ...filters, users: [] });
                    }}
                  />
                </BubbleFormGroup>
                <BubbleFormGroup labelClassName="text-white-i">
                  <BubbleCheckbox
                    variant="forest"
                    className="text-white"
                    checked={showOnlyArchived}
                    onValue={checked => {
                      setShowOnlyArchived(checked);
                    }}
                    label="Show Only Archived"
                  />
                </BubbleFormGroup>
              </div>
            </BubbleAccordion>
          </div>
          <div className="GroupsContent bg-blue-lightest overflow-y-auto flex-grow">
            <div className="GroupsHeader align-with-logo flex items-end px-5 sticky top-0 left-0 z-10 bg-blue-lightest">
              <div className="mb-3 w-full flex items-end px-3">
                <GroupsNavigation active="resources"></GroupsNavigation>
              </div>
            </div>

            <div className="GroupsBody mx-5 content-minus-logo min-w-900">
              <BubbleOverlay loading={loading}>
                <BubbleTable
                  responsive
                  striped
                  headVariant="forest"
                  keyExtractor={item => item.id}
                  fields={tableFields}
                  items={filteredResources}
                  stickyHeader={130}
                  templates={tableTemplates}
                  showPretitle
                  pretitleClassName="p-0-i"
                  onRowClick={item => {
                    if (canCreateOrEditGroupResources(user, serverInfo)) handleEdit(item);
                  }}
                  pretitleRender={() => (
                    <div className="px-4 pt-4 pb-1 flex items-center">
                      <div className="flex-grow">
                        <BubbleInput
                          variant="forest"
                          inputClassName="outline-none-i"
                          type="text"
                          block
                          value={search}
                          onValue={setSearch}
                          placeholder="Search..."
                        />
                      </div>
                    </div>
                  )}
                />
              </BubbleOverlay>
              <div className="mt-3">&nbsp;</div>
            </div>
          </div>
          {mode.type !== 'default' && (
            <BubbleDrawer hideTools containerClassName="bg-eggshell">
              <GroupResourceForm
                types={resourceData.resourceTypes.map(r => r.name)}
                mode={mode}
                onClose={handleCloseGroupResourceForm}
                onSubmitGroupResource={refresh}
              />
            </BubbleDrawer>
          )}
        </Page>
      </PageWrapper>
    </>
  );
}
